import { TypedSopNode } from "./_Base";
import { CoreObject } from "../../../core/geometry/Object";
import { CoreInstancer } from "../../../core/geometry/Instancer";
import { CoreString } from "../../../core/String";
import { SopCopyStamp } from "./utils/CopyStamp";
import { Matrix4 } from "three";
import { NodeParamsConfig, ParamConfig } from "../utils/params/ParamsConfig";
import { InputCloneMode } from "../../poly/InputCloneMode";
import { TypeAssert } from "../../poly/Assert";
import { isBooleanTrue } from "../../../core/BooleanValue";
import { CoreAttribute } from "../../../core/geometry/Attribute";
import { CoreTransform, RotationOrder } from "../../../core/Transform";
var TransformMode = /* @__PURE__ */ ((TransformMode2) => {
  TransformMode2[TransformMode2["OBJECT"] = 0] = "OBJECT";
  TransformMode2[TransformMode2["GEOMETRY"] = 1] = "GEOMETRY";
  return TransformMode2;
})(TransformMode || {});
const TRANSFORM_MODES = [0 /* OBJECT */, 1 /* GEOMETRY */];
const TransformModeMenuEntries = [
  { name: "object", value: 0 /* OBJECT */ },
  { name: "geometry", value: 1 /* GEOMETRY */ }
];
class CopySopParamsConfig extends NodeParamsConfig {
  constructor() {
    super(...arguments);
    this.count = ParamConfig.INTEGER(1, {
      range: [1, 20],
      rangeLocked: [true, false]
    });
    this.t = ParamConfig.VECTOR3([0, 0, 0]);
    this.r = ParamConfig.VECTOR3([0, 0, 0]);
    this.s = ParamConfig.VECTOR3([1, 1, 1]);
    this.scale = ParamConfig.FLOAT(1);
    this.transformOnly = ParamConfig.BOOLEAN(0);
    this.transformMode = ParamConfig.INTEGER(0, {
      menu: {
        entries: TransformModeMenuEntries
      }
    });
    this.copyAttributes = ParamConfig.BOOLEAN(0);
    this.attributesToCopy = ParamConfig.STRING("", {
      visibleIf: { copyAttributes: true }
    });
    this.useCopyExpr = ParamConfig.BOOLEAN(1);
  }
}
const ParamsConfig = new CopySopParamsConfig();
export class CopySopNode extends TypedSopNode {
  constructor() {
    super(...arguments);
    this.paramsConfig = ParamsConfig;
    this._attribNamesToCopy = [];
    this._objects = [];
    this._instancer = new CoreInstancer();
    this._instanceMatrix = new Matrix4();
    this._coreTransform = new CoreTransform();
    this._transformAccumulatedMatrix = new Matrix4();
    this._transformMatrix = new Matrix4();
  }
  static type() {
    return "copy";
  }
  static displayedInputNames() {
    return ["geometry to be copied", "points to copy to (optional)"];
  }
  initializeNode() {
    this.io.inputs.setCount(1, 2);
    this.io.inputs.initInputsClonedState([InputCloneMode.FROM_NODE, InputCloneMode.NEVER]);
  }
  async cook(inputCoreGroups) {
    if (!isBooleanTrue(this.pv.useCopyExpr)) {
      this.stampNode().reset();
    }
    const coreGroup0 = inputCoreGroups[0];
    if (!this.io.inputs.hasInput(1)) {
      await this.cookWithoutTemplate(coreGroup0);
      return;
    }
    const coreGroup1 = inputCoreGroups[1];
    if (!coreGroup1) {
      this.states.error.set("second input invalid");
      return;
    }
    await this.cookWithTemplate(coreGroup0, coreGroup1);
  }
  async cookWithTemplate(instanceCoreGroup, template_core_group) {
    this._objects = [];
    const templatePoints = template_core_group.points();
    this._instancer.setCoreGroup(template_core_group);
    this._attribNamesToCopy = CoreString.attribNames(this.pv.attributesToCopy).map((attribName) => CoreAttribute.remapName(attribName));
    this._attribNamesToCopy = this._attribNamesToCopy.filter((attrib_name) => template_core_group.hasAttrib(attrib_name));
    await this._copyMovedObjectsOnTemplatePoints(instanceCoreGroup, templatePoints);
    this.setObjects(this._objects);
  }
  async _copyMovedObjectsOnTemplatePoints(instanceCoreGroup, templatePoints) {
    this._initAccumulatedTransform();
    for (let point_index = 0; point_index < templatePoints.length; point_index++) {
      await this._copyMovedObjectOnTemplatePoint(instanceCoreGroup, templatePoints, point_index);
      this._accumulateTransform();
    }
  }
  async _copyMovedObjectOnTemplatePoint(instanceCoreGroup, template_points, point_index) {
    this._instancer.matrixFromPoint(template_points[point_index], this._instanceMatrix);
    const templatePoint = template_points[point_index];
    if (isBooleanTrue(this.pv.useCopyExpr)) {
      this.stampNode().setPoint(templatePoint);
    }
    const movedObjects = await this._getMovedObjectsForTemplatePoint(instanceCoreGroup, point_index);
    for (let movedObject of movedObjects) {
      if (isBooleanTrue(this.pv.copyAttributes)) {
        this._copyAttributes_from_template(movedObject, templatePoint);
      }
      if (isBooleanTrue(this.pv.transformOnly)) {
        movedObject.applyMatrix4(this._instanceMatrix);
      } else {
        this._applyMatrixToObjectOrGeometry(movedObject, this._instanceMatrix);
      }
      this._applyAccumulatedTransform(movedObject);
      this._objects.push(movedObject);
    }
  }
  async _getMovedObjectsForTemplatePoint(instanceCoreGroup, pointIndex) {
    const stampedInstanceCoreGroup = await this._stampInstanceGroupIfRequired(instanceCoreGroup);
    if (stampedInstanceCoreGroup) {
      const getObjectsForTransformOnly = () => {
        const object = stampedInstanceCoreGroup.objects()[pointIndex];
        if (object) {
          return [object.clone()];
        } else {
          return [];
        }
      };
      const movedObjects = isBooleanTrue(this.pv.transformOnly) ? getObjectsForTransformOnly() : stampedInstanceCoreGroup.clone().objects();
      return movedObjects;
    } else {
      return [];
    }
  }
  async _stampInstanceGroupIfRequired(instance_core_group) {
    const container0 = await this.containerController.requestInputContainer(0);
    if (container0) {
      const core_group0 = container0.coreContent();
      if (core_group0) {
        return core_group0;
      } else {
        return;
      }
    } else {
      this.states.error.set(`input failed for index ${this.stampValue()}`);
      return;
    }
  }
  async _copyMovedObjectsForEachInstance(instance_core_group) {
    this._initAccumulatedTransform();
    for (let i = 0; i < this.pv.count; i++) {
      await this._copyMovedObjectsForInstance(instance_core_group, i);
      this._accumulateTransform();
    }
  }
  async _copyMovedObjectsForInstance(instance_core_group, i) {
    if (isBooleanTrue(this.pv.useCopyExpr)) {
      this.stampNode().setGlobalIndex(i);
    }
    const stamped_instance_core_group = await this._stampInstanceGroupIfRequired(instance_core_group);
    if (stamped_instance_core_group) {
      stamped_instance_core_group.objects().forEach((object) => {
        const clonedObject = CoreObject.clone(object);
        this._applyAccumulatedTransform(clonedObject);
        this._objects.push(clonedObject);
      });
    }
  }
  async cookWithoutTemplate(instance_core_group) {
    this._objects = [];
    await this._copyMovedObjectsForEachInstance(instance_core_group);
    this.setObjects(this._objects);
  }
  _copyAttributes_from_template(object, template_point) {
    this._attribNamesToCopy.forEach((attrib_name, i) => {
      const attrib_value = template_point.attribValue(attrib_name);
      const object_wrapper = new CoreObject(object, i);
      object_wrapper.addAttribute(attrib_name, attrib_value);
    });
  }
  stampValue(attrib_name) {
    return this.stampNode().value(attrib_name);
  }
  stampNode() {
    return this._stampNode = this._stampNode || this._createStampNode();
  }
  _createStampNode() {
    const stampNode = new SopCopyStamp(this.scene());
    this.dirtyController.setForbiddenTriggerNodes([stampNode]);
    return stampNode;
  }
  dispose() {
    super.dispose();
    if (this._stampNode) {
      this._stampNode.dispose();
    }
  }
  _initAccumulatedTransform() {
    const pv = this.pv;
    this._transformMatrix = this._coreTransform.matrix(pv.t, pv.r, pv.s, pv.scale, RotationOrder.XYZ);
    this._transformAccumulatedMatrix.identity();
  }
  _accumulateTransform() {
    this._transformAccumulatedMatrix.multiply(this._transformMatrix);
  }
  _applyAccumulatedTransform(object) {
    this._applyMatrixToObjectOrGeometry(object, this._transformAccumulatedMatrix);
  }
  _applyMatrixToObjectOrGeometry(object, matrix) {
    const transformMode = TRANSFORM_MODES[this.pv.transformMode];
    switch (transformMode) {
      case 0 /* OBJECT */: {
        this._applyMatrixToObject(object, matrix);
        return;
      }
      case 1 /* GEOMETRY */: {
        const geometry = object.geometry;
        if (geometry) {
          geometry.applyMatrix4(matrix);
        }
        return;
      }
    }
    TypeAssert.unreachable(transformMode);
  }
  _applyMatrixToObject(object, matrix) {
    object.matrix.multiply(matrix);
    object.matrix.decompose(object.position, object.quaternion, object.scale);
  }
}
